<div class="min-h-screen bg-gray-50">
  <!-- Header -->
  <app-header-nav></app-header-nav>

  <mat-toolbar class="border-b bg-white shadow-sm">
    <button mat-icon-button (click)="onBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h1 class="text-xl font-semibold text-gray-800 ml-4">QR Code Management</h1>
    <span class="flex-auto"></span>
    <button mat-raised-button color="primary" (click)="generateBulkQRCodes()">
      <mat-icon>qr_code</mat-icon> Generate Bulk
    </button>
  </mat-toolbar>

  <div class="h-screen flex items-center justify-center" *ngIf="isLoading">
    <mat-spinner></mat-spinner>
  </div>

  <div class="p-6 space-y-6" *ngIf="!isLoading">
    <!-- Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total QR Codes</mat-card-title>
          <mat-icon class="text-gray-500">qr_code</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold">{{ getTotalQRCodes() }}</div>
          <p class="text-xs text-gray-600">{{ getActiveQRCodes() }} active</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total Scans</mat-card-title>
          <mat-icon class="text-green-500">check_circle</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-green-500">{{ getTotalScans() }}</div>
          <p class="text-xs text-gray-600">All time</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Scanned Today</mat-card-title>
          <mat-icon class="text-blue-500">qr_code_scanner</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-blue-500">{{ getScannedToday() }}</div>
          <p class="text-xs text-gray-600">Tables accessed</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Active Tables</mat-card-title>
          <mat-icon class="text-green-500">check_circle</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold">{{ getActiveQRCodes() }}</div>
          <p class="text-xs text-gray-600">Ready for orders</p>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- QR Code Generation -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <mat-card>
        <mat-card-header>
          <mat-card-title>Generate Single QR Code</mat-card-title>
        </mat-card-header>
        <mat-card-content class="space-y-4">
          <mat-form-field class="w-full">
            <mat-label>Table Number</mat-label>
            <input matInput [(ngModel)]="newTableNumber" placeholder="Enter table number">
          </mat-form-field>
          <button mat-raised-button color="primary" class="w-full" (click)="generateQRCode()">
            <mat-icon>qr_code</mat-icon>
            Generate QR Code
          </button>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header>
          <mat-card-title>Bulk Generation</mat-card-title>
        </mat-card-header>
        <mat-card-content class="space-y-4">
          <mat-form-field class="w-full">
            <mat-label>Layout Template</mat-label>
            <mat-select [(ngModel)]="selectedLayout">
              <mat-option value="single">Single Code per Page</mat-option>
              <mat-option value="grid-4">4 Codes per Page</mat-option>
              <mat-option value="grid-9">9 Codes per Page</mat-option>
              <mat-option value="labels">Label Sheet</mat-option>
            </mat-select>
          </mat-form-field>
          <button mat-raised-button color="primary" class="w-full" (click)="generateBulkQRCodes()">
            <mat-icon>qr_code</mat-icon>
            Generate Tables 1-20
          </button>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- QR Code Management -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>Manage QR Codes</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <mat-card *ngFor="let qrCode of getQRCodes()" [class.border-green-200]="qrCode.isActive"
            [class.border-gray-200]="!qrCode.isActive">
            <mat-card-header class="pb-2">
              <mat-card-title class="text-lg flex items-center justify-between">
                <span>Table {{ qrCode.tableNumber }}</span>
                <mat-chip [color]="qrCode.isActive ? 'primary' : 'warn'">
                  {{ qrCode.isActive ? 'Active' : 'Inactive' }}
                </mat-chip>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content class="space-y-3">
              <!-- QR Code Preview -->
              <!-- The service returns a real scannable PNG in qrImageUrl (a
                   data: URI encoding /customer?bar=…&table=…). This used to show
                   a dashed placeholder box with an icon in its place, so the one
                   thing a bar needs from this screen — the actual code — was
                   never on it. -->
              <div class="w-32 h-32 mx-auto bg-white rounded-lg flex items-center justify-center">
                <img *ngIf="qrCode.qrImageUrl" [src]="qrCode.qrImageUrl"
                     [alt]="'QR code for table ' + qrCode.tableNumber"
                     class="w-32 h-32" [attr.data-qr-table]="qrCode.tableNumber">
                <div *ngIf="!qrCode.qrImageUrl"
                     class="w-32 h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <mat-icon class="text-gray-400 text-5xl">qr_code</mat-icon>
                </div>
              </div>

              <div class="text-center text-xs text-gray-600 space-y-1">
                <p>Scans: {{ qrCode.scanCount || 0 }}</p>
                <p *ngIf="qrCode.lastScanned">Last: {{ qrCode.lastScanned | date:'short' }}</p>
                <p>Created: {{ qrCode.createdAt | date:'short' }}</p>
              </div>

              <div class="flex gap-1">
                <button mat-stroked-button size="small" class="flex-1" (click)="copyQRContent(qrCode)">
                  <mat-icon>content_copy</mat-icon>
                </button>
                <button mat-stroked-button size="small" class="flex-1" (click)="downloadQR(qrCode)"
                        [attr.aria-label]="'Download the QR code for table ' + (qrCode.tableNumber || '')">
                  <mat-icon>download</mat-icon>
                </button>
                <button mat-stroked-button size="small" class="flex-1" (click)="printQR(qrCode)"
                        [attr.aria-label]="'Print the QR code for table ' + (qrCode.tableNumber || '')">
                  <mat-icon>print</mat-icon>
                </button>
                <button mat-icon-button (click)="toggleQRStatus(qrCode)">
                  <mat-icon>{{ qrCode.isActive ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Footer -->
  <app-footer></app-footer>
</div>